import { Tag, Tooltip } from 'antd'
import { MailOutlined } from '@ant-design/icons'
import { useTicketReplies } from '@/features/advisory-ai/hooks'
import { exactTime, when } from '../utils'

type TicketRepliesProps = {
  ticketId: string
  clientId: string
  customerName: string
}

export function TicketReplies({ ticketId, clientId, customerName }: TicketRepliesProps) {
  const query = useTicketReplies(ticketId, clientId)

  if (query.isLoading) return <div className="text-[12px] text-gray-400">Loading the thread…</div>
  if (query.isError) return <div className="text-[12px] text-gray-400">Could not load the thread.</div>
  if (!query.data?.length) {
    return <div className="text-[12px] text-gray-400">No messages yet.</div>
  }

  // The API sends oldest first; the console reads newest first.
  const newestFirst = [...query.data].reverse()

  return (
    <ol className="space-y-2" aria-label="Thread, newest first">
      {newestFirst.map((reply) => (
        <li
          key={reply.id}
          className={`rounded-lg border p-3 ${
            reply.fromSupport ? 'border-gray-200 bg-white' : 'border-[var(--feature-primary)] bg-gray-50'
          }`}
        >
          <div className="mb-1 text-[11.5px] text-gray-500">
            <span className="font-semibold text-gray-700">
              {reply.fromSupport
                ? `${reply.sentByName || 'Support'} (support)`
                : reply.sentByName || customerName || 'Customer'}
            </span>
            {' · '}
            <Tooltip title={exactTime(reply.sentAt)}>
              <span>{when(reply.sentAt)}</span>
            </Tooltip>
            {reply.sentByEmail && (
              <Tooltip title="Sent from the admin's own mail client. The customer was not emailed a notice for this message.">
                {/* The suppression is the operative fact and a Tag is not focusable,
                    so it has to be in the accessible name, not only the tooltip. */}
                <Tag
                  icon={<MailOutlined />}
                  className="ml-2 text-[10.5px]"
                  aria-label="Sent by email — the customer was not emailed a notice for this message"
                >
                  Sent by email
                </Tag>
              </Tooltip>
            )}
          </div>
          <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-gray-700">{reply.body}</p>
        </li>
      ))}
    </ol>
  )
}
